import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { EASE } from "../../lib/motion";
import { scGetOverview, scListSifs } from "../../lib/sifcontrolApi";

const stat = (label, value, testid) => (
    <div className="flex flex-col gap-1" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">{label}</div>
        <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-oth-ink">{value}</div>
    </div>
);

export default function SifControlHome() {
    const [overview, setOverview] = useState(null);
    const [q, setQ] = useState("");
    const [preview, setPreview] = useState([]);

    useEffect(() => {
        scGetOverview().then(setOverview).catch(() => setOverview(null));
        scListSifs({ limit: 8 }).then((r) => setPreview(r.items || [])).catch(() => setPreview([]));
    }, []);

    const submit = (e) => {
        e.preventDefault();
        const url = `/sifcontrol/sifs${q ? `?q=${encodeURIComponent(q)}` : ""}`;
        window.location.href = url;
    };

    const fmtAum = (cr) => {
        if (!cr || cr < 0.01) return "—";
        if (cr >= 100000) return `₹${(cr / 100000).toFixed(2)}L Cr`;
        return `₹${Math.round(cr).toLocaleString("en-IN")} Cr`;
    };

    return (
        <div>
            {/* Hero */}
            <section className="pt-14 md:pt-24 pb-10 md:pb-16" data-testid="sc-home-hero">
                <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.9, ease: EASE }}
                    >
                        <div className="text-[11px] uppercase tracking-[0.24em] text-oth-ink3 font-body mb-4">
                            SIF Control · by One Tree Hill
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-oth-ink leading-[1.02]">
                            Research. Compare.<br />
                            <span className="italic font-editorial font-normal">Rank SIFs.</span>
                        </h1>
                        <p className="mt-5 max-w-[620px] font-body text-oth-ink2 text-base md:text-lg">
                            A simple way to discover, compare and evaluate Specialized Investment Funds — powered by data, not marketing.
                        </p>

                        <form onSubmit={submit} className="mt-8 max-w-[560px] relative" data-testid="sc-home-search">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-oth-ink3" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="w-full h-12 md:h-14 pl-11 pr-32 rounded-full border border-oth-line bg-white text-sm md:text-base focus:outline-none focus:border-oth-ink transition"
                                placeholder="Search SIF, strategy or AMC"
                                data-testid="sc-home-search-input"
                            />
                            <button
                                type="submit"
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 btn-pill btn-pill-dark !py-2 !px-4 !text-xs"
                                data-testid="sc-home-search-submit"
                            >
                                Search <ArrowUpRight size={12} />
                            </button>
                        </form>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/sifcontrol/rankings" className="btn-pill btn-pill-dark" data-testid="sc-cta-rankings">
                                Explore Rankings <ArrowUpRight size={14} />
                            </Link>
                            <Link to="/sifcontrol/screener" className="btn-pill" data-testid="sc-cta-screener">
                                Screen SIFs
                            </Link>
                            <Link to="/sifcontrol/compare" className="btn-pill" data-testid="sc-cta-compare">
                                Compare SIFs
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Market overview */}
            <section className="pb-10 md:pb-14" data-testid="sc-home-overview">
                <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body mb-5">SIF Market</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 rounded-3xl border border-oth-line bg-white p-6 md:p-10">
                        {stat("Total SIFs", overview?.total_sifs ?? "—", "sc-stat-sifs")}
                        {stat("Total AMCs", overview?.total_amcs ?? "—", "sc-stat-amcs")}
                        {stat("Total AUM", overview ? fmtAum(overview.total_aum_cr) : "—", "sc-stat-aum")}
                        {stat("NAV Updated", overview?.latest_nav_date || "Awaiting first ingestion", "sc-stat-nav")}
                    </div>
                </div>
            </section>

            {/* Top-ranked preview */}
            <section className="pb-24" data-testid="sc-home-top">
                <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">Top ranked SIFs</div>
                            <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-oth-ink mt-1">
                                Highest scoring today
                            </div>
                        </div>
                        <Link to="/sifcontrol/rankings" className="text-sm font-body text-oth-ink3 hover:text-oth-ink" data-testid="sc-home-view-all">
                            View all rankings →
                        </Link>
                    </div>
                    <div className="rounded-3xl border border-oth-line bg-white overflow-hidden">
                        {preview.length === 0 ? (
                            <div className="p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-home-empty">
                                Rankings will appear here once the SIF master and NAV data are populated.
                            </div>
                        ) : (
                            <div className="divide-y divide-oth-line">
                                {preview.map((s, i) => (
                                    <Link
                                        to={`/sifcontrol/sif/${s.id}`}
                                        key={s.id}
                                        className="grid grid-cols-[40px_1fr_140px] md:grid-cols-[60px_1.4fr_1fr_100px_100px] items-center gap-4 px-5 py-4 hover:bg-oth-bg-2 transition-colors"
                                        data-testid={`sc-home-row-${i}`}
                                    >
                                        <div className="text-xs font-body text-oth-ink3">#{i + 1}</div>
                                        <div>
                                            <div className="font-display text-sm md:text-base font-medium text-oth-ink">{s.sif_name}</div>
                                            <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{s.sif_code}</div>
                                        </div>
                                        <div className="hidden md:block text-xs font-body text-oth-ink2">{s.strategy_category}</div>
                                        <div className="hidden md:block text-xs font-body text-oth-ink3">— </div>
                                        <div className="text-xs font-body text-oth-ink3 text-right">Unrated</div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
